import {
  ActionIcon,
  Drawer,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { Building2Icon, ExternalLinkIcon, UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  HumanDocument,
  MatterDocument,
  OrganizationDocument,
} from '@/database/baseTypes';
import { useUsers } from '@/database/hooks/hooks';
import { matterStatusSelectOptions } from '@/optionSets';
import { useMattersForClient } from '@/views/Contacts/hooks';

type ClientContact =
  | { type: 'human'; document: HumanDocument }
  | { type: 'organization'; document: OrganizationDocument };

interface Props {
  contact: ClientContact | undefined;
  opened: boolean;
  onClose: () => void;
}

const ClientDetailDrawer = ({ contact, opened, onClose }: Props) => {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position='right'
      size='lg'
      padding='lg'
      title={contact ? <DrawerTitle contact={contact} /> : null}
      scrollAreaComponent={ScrollArea.Autosize}
      styles={{
        header: {
          paddingTop: 'var(--mantine-spacing-sm)',
          paddingBottom: 'var(--mantine-spacing-sm)',
        },
      }}
    >
      {contact ? <DrawerBody contact={contact} /> : null}
    </Drawer>
  );
};

export default ClientDetailDrawer;

const DrawerTitle = ({ contact }: { contact: ClientContact }) => {
  const Icon = contact.type === 'human' ? UserIcon : Building2Icon;
  return (
    <Group gap='sm' wrap='nowrap'>
      <Icon size={20} />
      <Text fw={600} size='lg'>
        {displayNameFor(contact)}
      </Text>
    </Group>
  );
};

const DrawerBody = ({ contact }: { contact: ClientContact }) => {
  return (
    <Stack gap='lg'>
      <ContactInfo contact={contact} />
      <MattersSection contactId={contact.document.id} />
    </Stack>
  );
};

const ContactInfo = ({ contact }: { contact: ClientContact }) => {
  const { document } = contact;
  const emails = document.emailAddresses ?? [];
  const phones = document.phoneNumbers ?? [];

  if (emails.length === 0 && phones.length === 0) {
    return null;
  }

  return (
    <Stack gap='xs' mt='md'>
      {emails.map((email) => (
        <Text key={email.value} size='sm'>
          {email.value}
        </Text>
      ))}
      {phones.map((phone) => (
        <Text key={phone.value} size='sm'>
          {phone.value}
        </Text>
      ))}
    </Stack>
  );
};

const MattersSection = ({ contactId }: { contactId: string }) => {
  const mattersLoadState = useMattersForClient(contactId);

  if (mattersLoadState.status === 'loading') {
    return (
      <Group justify='center' py='xl'>
        <Loader color='pink.4' type='dots' size='lg' />
      </Group>
    );
  }

  if (mattersLoadState.status === 'error') {
    return (
      <Text size='sm' c='red'>
        Failed to load matters.
      </Text>
    );
  }

  const matters = mattersLoadState.data;

  return (
    <Stack gap='xs'>
      <Text fw={600} size='md' mt='md'>
        Matters
      </Text>
      {matters.length === 0 ? (
        <Text size='sm' c='dimmed' fs='italic'>
          This client has no matters yet.
        </Text>
      ) : (
        <MattersTable matters={matters} />
      )}
    </Stack>
  );
};

const MattersTable = ({ matters }: { matters: MatterDocument[] }) => {
  const users = useUsers();

  const userNameById = new Map<string, string>();
  for (const user of users ?? []) {
    userNameById.set(user.id, `${user.firstName} ${user.lastName}`.trim());
  }

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Responsible Attorney</Table.Th>
          <Table.Th>Date Opened</Table.Th>
          {/* Action column for the open-matter button — header left blank
              because the icon's tooltip already names the action. */}
          <Table.Th aria-label='Actions' w={40} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {matters.map((matter) => {
          const url = `/matter/${matter.id}`;
          return (
            <Table.Tr key={matter.id}>
              <Table.Td>{matter.name}</Table.Td>
              <Table.Td>{matterStatusLabel(matter.status)}</Table.Td>
              <Table.Td>
                {matter.responsibleAttorney
                  ? (userNameById.get(matter.responsibleAttorney) ?? '')
                  : ''}
              </Table.Td>
              <Table.Td>
                {matter.createdTimestamp
                  ? matter.createdTimestamp.toLocaleDateString()
                  : ''}
              </Table.Td>
              <Table.Td>
                {/* component={Link} renders ActionIcon as an <a href>, so
                    cmd/ctrl+click → new tab, right-click → "Open in New
                    Tab", etc. all work natively. */}
                <Tooltip label='Open this matter' withArrow>
                  <ActionIcon
                    component={Link}
                    to={url}
                    variant='subtle'
                    color='gray'
                    aria-label='Open this matter'
                  >
                    <ExternalLinkIcon size={16} />
                  </ActionIcon>
                </Tooltip>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};

const displayNameFor = (contact: ClientContact): string => {
  if (contact.type === 'human') {
    const { firstName, lastName } = contact.document;
    return `${firstName ?? ''} ${lastName ?? ''}`.trim();
  }
  return contact.document.companyName;
};

const matterStatusLabel = (status: MatterDocument['status']): string => {
  return (
    matterStatusSelectOptions.find((option) => option.value === status)
      ?.label ?? status
  );
};
